import Rover from '@rover/globals/Rover';
import { isAndroid, isiOS, isMobileEmbedded } from '@rover/rsdk/src/modules/Network/userAgent';
import emitMetric from '@rover/utilities/emitMetric';

/**
 * The only rating that arms the app store prompt. The Android bridge accepts a
 * four-star event too, but handles it as a no-op, so four stars can't arm
 * anything without an app release.
 */
export const FIVE_STARS = 5;

/**
 * The lowest rating a review page treats as positive. Pages record a rating
 * signal only at or above it, so each page contributes the same slice of its
 * traffic and their volumes can be compared as reviews move between them.
 */
export const POSITIVE_RATING = 4;

export const isPositiveRating = (stars: number): boolean => stars >= POSITIVE_RATING;

/**
 * Whether a five-star review on this device can reach something that arms the
 * prompt. Only the embedded Android app can: the web bridge exists nowhere
 * else, and iOS receives the arming event but forwards it to trackers without
 * acting on it. Reviews left anywhere else are recorded unarmed, so `armed`
 * counts prompts that can actually be shown rather than events sent.
 */
export const canArmPrompt = (): boolean => isMobileEmbedded() && isAndroid();

type AppRatingSignal = {
  /** What the person did to become a candidate, e.g. `owner_review_positive`. */
  trigger: string;
  /** Which review page emitted this, so the pages can be compared as traffic shifts between them. */
  source: string;
  /** The rating given, whether or not it is high enough to arm the prompt. */
  stars: number;
  /** Whether this review armed the prompt, which needs both five stars and a device that can be prompted. */
  armed: boolean;
};

const embeddedPlatform = (): string => {
  if (!isMobileEmbedded()) {
    return 'web';
  }
  if (isAndroid()) {
    return 'android';
  }
  return isiOS() ? 'ios' : 'other';
};

/**
 * Records one review that could arm the native app store rating prompt.
 *
 * Every review page that can arm the prompt emits this, armed or not, so a
 * single metric answers both how many people we ask for a store rating and how
 * many we could ask. Tagged with country because the prompt is enabled one
 * market at a time, and with platform because only the Android app acts on the
 * arming event.
 *
 * This is the last thing we can see. Arming writes a flag the app reads on its
 * next native screen, and the prompt itself is drawn by the OS, so neither the
 * ask nor its outcome is observable from here.
 */
export default function emitAppRatingSignal({
  trigger,
  source,
  stars,
  armed,
}: AppRatingSignal): void {
  emitMetric('app_rating_prompt_signal', {
    trigger,
    source,
    platform: embeddedPlatform(),
    country: Rover.context?.appRatingPromptCountry || 'unknown',
    stars: String(stars),
    armed: String(armed),
  });
}
